import type { ArtifactAction } from "@/types/artifacts.ts";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useRef } from "react";

/**
 * The omnibox biscuit's live review queue (≥2 surfaced artifacts): the ids under
 * review in carousel order, plus the handlers to move the review along and to
 * submit what's left of it.
 *
 * While a queue is open a decision is *staged* rather than fired: three drafts
 * are one review, and they submit as one request (BIG-7346). Staging is not
 * silence — the surface shows the card as decided, and staging on the last card
 * commits the batch there and then, which is the whole of what "the third Send
 * does nothing" was asking for (BIG-7171).
 *
 * The canvas presents queued artifacts too, but it resolves them by URN from an
 * arbitrary thread and knows nothing about the biscuit's queue state. Rather
 * than thread that state through the split (the queue lives inside
 * `MessageList`, the canvas is its sibling), the queue publishes here and the
 * canvas reads it to stage rather than fire, and to page through the review from
 * its own header.
 *
 * A plain jotai atom, mirroring `canvasUpdateBadge`: purely client-side,
 * non-persisted, and readable across two sibling subtrees without a shared
 * provider.
 */
type ArtifactReviewQueue = {
  /** Ids under review, in carousel order — the pager's index and total. */
  ids: readonly string[];
  /** Staging is addressed by artifact id, so a surface stages for the artifact
   *  it is actually showing — not for whichever card the carousel sits on. The
   *  queue owner decides what follows: step to the next card, or, on the last
   *  one, commit the batch. */
  stageAction: (artifactId: string, action: ArtifactAction) => void;
  /** The action staged on each card so far, so a surface can show its own card
   *  as decided. Without it the canvas would keep offering a live Send for a
   *  draft already in the batch, and the user would queue it twice. */
  staged: ReadonlyMap<string, ArtifactAction>;
  /** Move the review to another queued artifact. Navigation only: it never
   *  commits, so paging through cards on the canvas can't submit the review out
   *  from under the user. */
  setActive: (artifactId: string) => void;
  /** Submit what was staged, as one agent run, leaving undecided cards pending —
   *  the queue's single commit, behind the "Skip" affordance each surface offers
   *  on the last card. */
  commit: () => void;
};

const NO_QUEUE: ArtifactReviewQueue = {
  ids: [],
  stageAction: () => {},
  staged: new Map(),
  setActive: () => {},
  commit: () => {},
};

/** A published queue plus the identity of the surface that published it. */
type PublishedQueue = ArtifactReviewQueue & { token: symbol };

/**
 * Every mounted publisher, in mount order — not one slot.
 *
 * Two surfaces can be mounted at once (React mounts the incoming thread's
 * surface before unmounting the outgoing one), and a single slot cannot survive
 * that in either direction: a departing publisher's teardown either wipes the
 * arriving one's queue, or — if it only clears what it owns — the *newer* one
 * unmounting leaves the slot empty while the older review is still live and
 * still needs it. The older publisher has no way to notice and re-publish; its
 * effect deps have not changed. Keeping every publisher means a release simply
 * uncovers whoever is still there.
 */
const publishedQueuesAtom = atom<readonly PublishedQueue[]>([]);

/** What surfaces read: the most recently mounted publisher still standing. */
const artifactReviewQueueAtom = atom<ArtifactReviewQueue>(
  (get) => get(publishedQueuesAtom).at(-1) ?? NO_QUEUE,
);

/**
 * Publish the review queue for as long as the calling surface is mounted,
 * clearing it on unmount. Pass `undefined` ids when no queue is live (a single
 * artifact, or a dismissed overlay) — a lone artifact keeps fire-on-click.
 *
 * A publisher only ever adds, updates or removes its own entry — never anyone
 * else's — so two surfaces can overlap in either order and the live review
 * always wins. Identity is a token rather than the id set, since two surfaces
 * can legitimately publish the same artifacts. This is not cosmetic: a canvas
 * that stops seeing its artifact as under review fires the action on the spot
 * instead of staging it, splitting the batch this path exists to keep together
 * (BIG-7346).
 */
export function usePublishArtifactReviewQueue(
  artifactIds: readonly string[] | undefined,
  queue: Omit<ArtifactReviewQueue, "ids">,
): void {
  const setQueues = useSetAtom(publishedQueuesAtom);
  // Stable for this hook instance's lifetime — it is the publisher's identity.
  const tokenRef = useRef<symbol>(undefined);
  tokenRef.current ??= Symbol("artifactReviewQueue");
  const token = tokenRef.current;
  // Collapse to a primitive so the effect tracks the queue's *contents*, not the
  // new array identity every render produces. `queue` is the caller's memoized
  // object, so it only re-publishes when a handler or the staged batch changes.
  const key = (artifactIds ?? []).join(",");
  useEffect(() => {
    const release = () => setQueues((prev) => prev.filter((q) => q.token !== token));
    // Nothing to publish — a surface with no queue holds no entry at all.
    if (key === "") {
      release();
      return;
    }
    const published: PublishedQueue = { ...queue, ids: key.split(","), token };
    setQueues((prev) => {
      const index = prev.findIndex((q) => q.token === token);
      // Update in place rather than re-appending: position is mount order, and
      // moving on every re-publish would let a background surface steal the
      // reader each time its staged set changed.
      if (index === -1) return [...prev, published];
      const next = prev.slice();
      next[index] = published;
      return next;
    });
    return release;
  }, [key, queue, setQueues, token]);
}

/**
 * The ids under review right now, or an empty list when no review is open. For
 * surfaces that need to know a review is in progress without taking part in it —
 * the canvas socket, which has to leave a card the user is reviewing alone.
 */
export function useArtifactReviewQueueIds(): readonly string[] {
  return useAtomValue(artifactReviewQueueAtom).ids;
}

/**
 * The stage handler for an artifact awaiting a decision in a live review queue,
 * or `undefined` when it isn't queued (so the caller fires normally, as a lone
 * artifact always has). Wire it into `ArtifactActionsFooter`'s `onStage` to keep
 * a decision inside the batch.
 */
export function useArtifactReviewStaging(
  artifactId: string | undefined,
): ((action: ArtifactAction) => void) | undefined {
  const queue = useAtomValue(artifactReviewQueueAtom);
  const isQueued = artifactId !== undefined && queue.ids.includes(artifactId);
  return useMemo(
    () =>
      isQueued && artifactId !== undefined
        ? (action: ArtifactAction) => queue.stageAction(artifactId, action)
        : undefined,
    [isQueued, artifactId, queue],
  );
}

/**
 * The action this artifact has staged in the live review, if any — what turns a
 * surface's Send row into a "queued" one.
 */
export function useArtifactStagedAction(
  artifactId: string | undefined,
): ArtifactAction | undefined {
  const queue = useAtomValue(artifactReviewQueueAtom);
  return artifactId === undefined ? undefined : queue.staged.get(artifactId);
}

/** A surface's position in the review queue, shaped like the biscuit's pager. */
export type ArtifactReviewPagination = {
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  /** Submit what was staged, leaving undecided cards pending. Offered on the last
   *  card, where there is nothing left to advance to — the biscuit's "Skip",
   *  reachable from the canvas too. */
  onSkip: () => void;
};

/**
 * Where this artifact sits in the live review queue, so a surface showing it can
 * page through the queue the way the biscuit's carousel does — `undefined` when
 * it isn't queued, which is also every single-artifact case (the biscuit only
 * publishes a queue for ≥2).
 *
 * Stepping writes back to the queue owner, so the biscuit and the canvas move
 * together: the biscuit re-renders on the new artifact and carries the canvas
 * along with it.
 */
export function useArtifactReviewPagination(
  artifactId: string | undefined,
): ArtifactReviewPagination | undefined {
  const queue = useAtomValue(artifactReviewQueueAtom);
  const index = artifactId === undefined ? -1 : queue.ids.indexOf(artifactId);
  return useMemo(() => {
    if (index < 0 || queue.ids.length < 2) return undefined;
    const step = (delta: number) => {
      const next = queue.ids[index + delta];
      if (next !== undefined) queue.setActive(next);
    };
    return {
      index,
      total: queue.ids.length,
      onPrev: () => step(-1),
      onNext: () => step(1),
      onSkip: () => queue.commit(),
    };
  }, [queue, index]);
}
